import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Business,
  Receipt,
  Payment,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  getDashboardSummary,
  getRevenueTrend,
  getRevenueByTenant,
  getRecentTransactions,
  getPendingPayouts,
  DashboardSummary,
  RevenueTrendItem,
  TenantRevenue,
  RecentTransaction,
  PendingPayout,
} from '@/services/api/dashboardService';

// 金額フォーマット
const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

// 日付フォーマット
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// サマリーカード
interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color?: string;
}

const SummaryCard = ({ title, value, subtitle, icon, trend, color = '#1976d2' }: SummaryCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend >= 0 ? (
                <TrendingUp sx={{ color: 'success.main', fontSize: 20, mr: 0.5 }} />
              ) : (
                <TrendingDown sx={{ color: 'error.main', fontSize: 20, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: trend >= 0 ? 'success.main' : 'error.main' }}
              >
                {trend >= 0 ? '+' : ''}{trend}% 前月比
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}15`,
            p: 1.5,
            borderRadius: 2,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrendItem[]>([]);
  const [tenantRevenue, setTenantRevenue] = useState<TenantRevenue[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [pendingPayouts, setPendingPayouts] = useState<{ data: PendingPayout[]; total_amount: number; count: number }>({
    data: [],
    total_amount: 0,
    count: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryData, trendData, tenantData, txData, payoutData] = await Promise.all([
          getDashboardSummary(),
          getRevenueTrend(6),
          getRevenueByTenant(5),
          getRecentTransactions(5),
          getPendingPayouts(),
        ]);

        setSummary(summaryData);
        setRevenueTrend(trendData);
        setTenantRevenue(tenantData);
        setRecentTransactions(txData);
        setPendingPayouts(payoutData);
      } catch (err) {
        setError('データの取得に失敗しました');
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // テナント収益の最大値（プログレスバー用）
  const maxTenantRevenue = Math.max(...tenantRevenue.map((t) => t.total_revenue), 1);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        ダッシュボード
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        プラットフォーム全体の収益・状況
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="今月の収益"
            value={formatCurrency(summary?.this_month_revenue || 0)}
            trend={summary?.growth_rate}
            icon={<AccountBalance sx={{ fontSize: 32 }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="総収益"
            value={formatCurrency(summary?.total_revenue || 0)}
            subtitle={`${summary?.this_month_transactions || 0}件の取引（今月）`}
            icon={<Receipt sx={{ fontSize: 32 }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="未払い支払い"
            value={formatCurrency(summary?.pending_payouts || 0)}
            subtitle={`${summary?.pending_payout_count || 0}件`}
            icon={<Payment sx={{ fontSize: 32 }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="アクティブテナント"
            value={`${summary?.active_tenants || 0}`}
            subtitle={`総ユーザー: ${summary?.total_users || 0}名`}
            icon={<Business sx={{ fontSize: 32 }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* グラフエリア */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* 月別収益推移 */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                月別収益推移
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={(v) => `¥${(v / 10000).toFixed(0)}万`} />
                    <Tooltip
                      formatter={(value: number) => [formatCurrency(value), '収益']}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Bar dataKey="revenue" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* テナント別収益 */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                テナント別収益 TOP5
              </Typography>
              {tenantRevenue.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  データがありません
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {tenantRevenue.map((tenant, index) => (
                    <Box key={tenant.id} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {index + 1}. {tenant.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(tenant.total_revenue)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(tenant.total_revenue / maxTenantRevenue) * 100}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* テーブルエリア */}
      <Grid container spacing={3}>
        {/* 最近の取引 */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                最近の取引
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>日時</TableCell>
                      <TableCell>テナント</TableCell>
                      <TableCell>顧客</TableCell>
                      <TableCell align="right">金額</TableCell>
                      <TableCell>ステータス</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          取引データがありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentTransactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(tx.transaction_date)}
                          </TableCell>
                          <TableCell>{tx.tenant_name || '-'}</TableCell>
                          <TableCell>
                            {tx.customer_name || tx.customer_email || '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                            {formatCurrency(tx.gross_amount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tx.status}
                              size="small"
                              color={tx.status === 'completed' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 未払い支払い */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  未払い支払い
                </Typography>
                <Chip
                  label={`${pendingPayouts.count}件 / ${formatCurrency(pendingPayouts.total_amount)}`}
                  color="warning"
                  size="small"
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>受取人</TableCell>
                      <TableCell align="right">金額</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingPayouts.data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          未払いの支払いはありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingPayouts.data.slice(0, 5).map((payout) => (
                        <TableRow key={payout.id}>
                          <TableCell>
                            <Typography variant="body2">{payout.recipient_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payout.tenant_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                            {formatCurrency(payout.net_amount)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
